import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { 
  Tag, Printer, Download, Plus, Package,
  CheckSquare, Loader2, Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

export default function LabelGenerator() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [batches, setBatches] = useState([]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [templatesRes, productsRes] = await Promise.all([
        api.get('/labels/templates'),
        api.get('/products')
      ]);
      
      setTemplates(templatesRes.data || []);
      setProducts(productsRes.data?.items || []);
      
      if (templatesRes.data?.length > 0) {
        setSelectedTemplate(templatesRes.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const toggleProduct = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const generateLabels = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Please select at least one product');
      return;
    }
    
    if (!selectedTemplate) {
      toast.error('Please select a label template');
      return;
    }

    setGenerating(true);
    try {
      const res = await api.post('/labels/generate-for-products', {
        product_ids: selectedProducts,
        template_id: selectedTemplate
      });
      
      toast.success(`Generated labels for ${selectedProducts.length} products`);
      setBatches(prev => [res.data, ...prev]);
      setSelectedProducts([]);
    } catch (error) {
      toast.error('Failed to generate labels');
    } finally {
      setGenerating(false);
    }
  };

  const downloadBatch = (batchId) => {
    window.open(`${api.defaults.baseURL}/labels/batches/${batchId}/download`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Label Generator</h1>
          <p className="text-muted-foreground">Generate shelf labels, price tags, and barcodes</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Template Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5" />
              Label Template
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {templates.map((template) => (
                <div
                  key={template.id}
                  onClick={() => setSelectedTemplate(template.id)}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedTemplate === template.id
                      ? 'border-cyan-500 bg-cyan-500/10'
                      : 'border-border hover:border-cyan-500/50'
                  }`}
                >
                  <p className="font-medium">{template.name}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {template.include_name && <Badge variant="outline">Name</Badge>}
                    {template.include_price && <Badge variant="outline">Price</Badge>}
                    {template.include_barcode && <Badge variant="outline">Barcode</Badge>}
                    {template.include_sku && <Badge variant="outline">SKU</Badge>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Product Selection */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Select Products
              <span className="text-sm font-normal text-muted-foreground ml-2">
                ({selectedProducts.length} selected)
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left">
                      <input
                        type="checkbox"
                        checked={selectedProducts.length === products.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedProducts(products.map(p => p.id));
                          } else {
                            setSelectedProducts([]);
                          }
                        }}
                        className="rounded"
                      />
                    </th>
                    <th className="px-3 py-2 text-left text-sm font-medium">Product</th>
                    <th className="px-3 py-2 text-left text-sm font-medium">SKU</th>
                    <th className="px-3 py-2 text-left text-sm font-medium">Price</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {products.map((product) => (
                    <tr key={product.id} className="hover:bg-muted/50">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedProducts.includes(product.id)}
                          onChange={() => toggleProduct(product.id)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {product.image_url && (
                            <img 
                              src={product.image_url} 
                              alt=""
                              className="w-8 h-8 rounded object-cover"
                            />
                          )}
                          <span>{product.name}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-600">
                        {product.sku}
                      </td>
                      <td className="px-3 py-2 font-medium">
                        ${product.price?.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex justify-end">
              <Button
                onClick={generateLabels}
                disabled={generating || selectedProducts.length === 0}
              >
                {generating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Printer className="w-4 h-4 mr-2" />
                )}
                Generate {selectedProducts.length > 0 && `(${selectedProducts.length})`} Labels
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Batches */}
      {batches.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Recent Label Batches</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {batches.map((batch) => (
                <div 
                  key={batch.batch_id}
                  className="flex items-center justify-between p-4 bg-muted rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-background rounded border flex items-center justify-center">
                      <Tag className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-medium">Batch #{batch.batch_id.slice(0, 8)}</p>
                      <p className="text-sm text-muted-foreground">
                        {batch.item_count} labels • Created {new Date(batch.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadBatch(batch.batch_id)}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
